import { useState, useEffect } from 'react';

import { useToast } from '@chakra-ui/react';

import { deletePickups } from 'api/pickup';
import { useMutation, useQueryClient } from 'react-query';

import { washHub } from 'store/slices';
import { useWashHubListener } from 'hooks';

import { addDays, subDays } from 'date-fns';

import { PickupsProps } from './Pickups.props';

import PickupsView from './Pickups.view';

import useQueryParams from 'hooks/useQueryParams';
import useUrlSharing from 'hooks/useUrlSharing';

const PickupsContainer = (_: PickupsProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const queryUrl = useQueryParams();

  const [endDate, setEndDate] = useState<Date>();
  const [startDate, setStartDate] = useState<Date>();
  const [status, setStatus] = useState<string>();
  const [venue, setVenue] = useState<string[]>();
  const [defaultIndex, setDefaultIndex] = useState<number>(0);
  const [isUrlMapped, setIsUrlMapped] = useState<boolean>(false);
  const [urlRecord, setUrlRecord] = useState({});

  const washHubId = washHub.selectors.useSelectedWashHubId();

  const isUpdated = useUrlSharing(urlRecord, washHubId);

  const { mutate, isLoading: isDeleting } = useMutation(deletePickups);

  useEffect(() => {
    const urlParams = {
      washHubId: String(washHubId),
      ...(status ? { status } : {}),
      ...(defaultIndex ? { defaultIndex: `${defaultIndex}` } : {}),
      ...(startDate ? { startDate: `${startDate.valueOf()}` } : {}),
      ...(endDate ? { endDate: `${endDate.valueOf()}` } : {}),
      ...(venue ? { venue: venue.join('-') } : {}),
    };
    setUrlRecord(urlParams);
  }, [washHubId, endDate, startDate, status, venue, defaultIndex]);

  useEffect(() => {
    const { status, venue, defaultIndex, startDate, endDate } = queryUrl;
    status && setStatus(status);
    venue && setVenue(venue.split('-'));
    defaultIndex && setDefaultIndex(+defaultIndex || 0);
    startDate && setStartDate(new Date(+startDate));
    endDate && setEndDate(new Date(+endDate));

    if (isUpdated) setIsUrlMapped(true);
  }, [isUpdated]);

  useWashHubListener(() => {
    setStatus(undefined);
    setStartDate(undefined);
    setEndDate(undefined);

    onClearFilters();
  });

  const onSearchPress = (startDate: Date | null, endDate: Date | null) => {
    if (startDate) {
      setStartDate(startDate);
    }

    if (endDate) {
      setEndDate(endDate);
    }

    setStatus(undefined);
  };

  const onSearchClear = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setStatus(undefined);
  };

  const onStatusClick = (_status?: string) => {
    setStatus(_status);

    const today = new Date();

    if (_status === 'overdue') {
      const yesterday = subDays(today, 1);

      setEndDate(yesterday);
      setStartDate(undefined);
    } else if (_status === 'today') {
      setEndDate(today);
      setStartDate(today);
    } else if (_status === 'tomorrow') {
      const tomorrow = addDays(today, 1);

      setEndDate(tomorrow);
      setStartDate(tomorrow);
    } else if (_status === 'future') {
      const tomorrow = addDays(today, 1);

      setEndDate(undefined);
      setStartDate(tomorrow);
    } else {
      setStartDate(undefined);
      setEndDate(undefined);
    }
  };

  const onDelete = (ids: string[], _onSuccess: () => void) =>
    mutate(ids, {
      onSuccess: () => {
        toast({
          duration: 3000,
          isClosable: true,
          title: 'Success',
          status: 'success',
          description: 'Pickups Deleted',
        });

        queryClient.invalidateQueries(`fetch-pickups-${washHubId}`);

        _onSuccess();
      },
    });

  const onApplyFilters = (data: string[], type: string) => {
    setVenue(data);
  };

  const onClearFilters = () => {
    setVenue(undefined);
  };

  const _props = {
    status,
    venue,
    startDate,
    endDate,
    isDeleting,
    defaultIndex,
    setDefaultIndex,
    onDelete,
    onSearchPress,
    onSearchClear,
    onStatusClick,
    onApplyFilters,
    onClearFilters,
  };

  return <>{isUrlMapped && <PickupsView {..._props} />}</>;
};

export default PickupsContainer;
